import {
	changeFilterSelect,
	changePage,
	changeSlice,
	changeSort,
	getLog,
	getLogRejected,
	getLogResolved,
	getRiskConfig,
	getRiskConfigResolved,
	getRiskDetails,
	getRiskDetailsRejected,
	getRiskDetailsResolved,
	reloadRiskDetails,
	resetRisk,
	resetRiskRejected,
	resetRiskResolved,
	runRisk,
	runRiskRejected,
	runRiskResolved,
	setRiskDetailsIsBusy,
	setRiskSetupDirty,
	saveRiskConfigAction,
	createRiskConfig,
	setRiskConfigNameError,
	setRiskConfigIsCreating,
	setRiskIsSaving,
	renameRiskConfigAction,
	deleteRisk,
	setRiskIsDeleting,
	duplicateRiskConfig,
	changeRiskConfigPublishState,
	getRiskCountResolved,
	getRiskCountInspection,
	getRiskCountAttribute,
	getRiskCountProximity,
	getRiskCountProximityAttribute,
	getRiskCountCustomTable,
	setRiskCountIsLoading,
	getSimulationData,
	getSimulationDataResolved,
	getRiskCountScoreMatrix,
	initRiskMatrixPreview,
	setIsPreviewLoading,
	setMatrixPreviewStatus,
	getRiskMatrixPreview,
	setRiskMatrixAfterPreview,
	setDidMatrixUpdate,
	getTaskDataResolved,
	getTaskData,
	setIsPreviewOnLoad,
	getRiskCountObservation,
	getRiskCountTask,
	setEnablePreview,
	setOnlyGradesUpdated,
	cancelRiskMatrixPreview,
	setCancellPreview,
	changePagination,
	changeFiltersSelect,
} from '@Actions/Risk.actions';
import { error, success } from '@innovyze/stylovyze';
import { put, retry, takeLatest } from '@redux-saga/core/effects';
import { AnyAction, PayloadAction } from '@reduxjs/toolkit';
import {
	saveRiskConfigApi,
	getRiskCalcLogApi,
	getRiskConfigApi,
	getRiskDetailsApi,
	resetRiskCalculationApi,
	startRiskCalculationApi,
	createRiskConfigApi,
	deleteRiskApi,
	duplicateRiskConfigApi,
	changeRiskConfigPublishStateApi,
	getRiskCountInspectionApi,
	getRiskCountAtributeApi,
	getRiskCountProximityAtributeApi,
	getRiskCountProximityApi,
	getRiskCountCustomTableApi,
	getSimulationDataApi,
	getRiskCountScoreMatrixApi,
	getRiskPreviewApi,
	initRiskPreviewApi,
	getTaskDataApi,
	getRiskCountObservationApi,
	getRiskCountTaskApi,
	resetRiskPreviewCalculationApi,
} from '@Services/Risk/Risk.services';
import {
	Category,
	ComponentCountReply,
	CreateConfig,
	InitRiskMatrixPreviewResponse,
	LogReply,
	RiskConfig,
	RiskRunReply,
	SimulationData,
	TaskData,
} from '@Types';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { select, call, delay } from 'redux-saga/effects';
import { ResolvedResponse, Row } from '@innovyze/lib_am_common';
import { riskDetailsQuerySelector } from '@Selectors';
import { RiskConfig as MongoRiskConfig } from '../types/riskConfig.types';
import { t } from 'i18next';
import { getLoFConfigResolved } from '@Actions/LoF.actions';
import { getCoFComponentsResolved } from '@Actions/CoF.actions';
import { addRouteNamespace } from '@Utils/actions';
import { defaultRiskMatrix } from '@Reducers/RiskMatrixDefaultScores';
import { setRiskRunning } from '@Actions/util.actions';
import { changeRiskConfigsPage } from '@innovyze/lib_am_common/Actions';

export function* getRiskConfigSaga(action: AnyAction) {
	const configId = action.payload;
	const mongoRiskConfigResponse: AxiosResponse<MongoRiskConfig> = yield call(
		getRiskConfigApi,
		configId,
	);

	// TODO: This should all get re-factored at some point to make the code neater
	const snowRiskConfig = mapMongoCfgToSnowCfg(mongoRiskConfigResponse.data);

	yield put(getRiskConfigResolved(snowRiskConfig));

	// Now copy the information into the LOF and COF redux
	const defaultLofCategory: Category = {
		name: t('Condition Category'),
		isCondition: true,
		useThisOnly: false,
		weighting: snowRiskConfig.riskSetting.assetType === 'pipe' ? 0 : 100,
		useHighestScore: false,
		components: [],
	};

	yield put(
		getLoFConfigResolved(
			snowRiskConfig.LOFCategories.length
				? snowRiskConfig.LOFCategories
				: [defaultLofCategory],
		),
	);

	yield put(getCoFComponentsResolved(snowRiskConfig.COFCategories));
	yield put(getCoFComponentsResolved(snowRiskConfig.COFCategories));
}

const mapMongoCfgToSnowCfg = (mongoCfg: MongoRiskConfig): RiskConfig => {
	const result: RiskConfig = {
		riskSetting: {
			configId: mongoCfg.configId,
			inspectionStandard: mongoCfg.inspectionStandard,
			publishState: mongoCfg.publishState,
			RiskMethod: mongoCfg.riskMethod,
			COFWeighting: mongoCfg.cofWeighting,
			LOFWeighting: mongoCfg.lofWeighting,
			LastRun: mongoCfg.lastRun,
			// Use the lastRun if the completed date isn't there for older runs
			lastRunCompleted: mongoCfg.lastRunCompleted ?? mongoCfg.lastRun,
			Grades: {
				Low: mongoCfg.grades.low,
				Medium: mongoCfg.grades.medium,
				High: mongoCfg.grades.high,
				Max: mongoCfg.grades.max,
				Extreme: mongoCfg.grades.extreme,
			},
			assetType: mongoCfg.assetType,
			name: mongoCfg.name,
			assetCount: mongoCfg.assetCount,
			runBy: mongoCfg.runBy,
			status: mongoCfg.status,
			riskType: mongoCfg.riskType ?? 'linear',
			matrix: mongoCfg.matrix ?? { ...defaultRiskMatrix },
			maxOverallRiskScore: mongoCfg.maxOverallRiskScore,
			postProcessing: mongoCfg.postProcessing,
		},
		LOFCategories: mongoCfg.lofCfgCategories,
		COFCategories: mongoCfg.cofCfgCategories,
	};

	return result;
};

export const mapSnowCfgToMongoCfg = (snowCfg: RiskConfig): MongoRiskConfig => {
	const result: MongoRiskConfig = {
		publishState: snowCfg.riskSetting.publishState,
		inspectionStandard: snowCfg.riskSetting.inspectionStandard,
		configId: snowCfg.riskSetting.configId,
		name: snowCfg.riskSetting.name,
		cofWeighting: snowCfg.riskSetting.COFWeighting,
		lofWeighting: snowCfg.riskSetting.LOFWeighting,
		riskMethod: snowCfg.riskSetting.RiskMethod,
		lastRun: snowCfg.riskSetting.LastRun,
		grades: {
			low: snowCfg.riskSetting.Grades.Low,
			medium: snowCfg.riskSetting.Grades.Medium,
			high: snowCfg.riskSetting.Grades.High,
			extreme: snowCfg.riskSetting.Grades.Extreme,
			max: snowCfg.riskSetting.Grades.Max,
		},
		assetType: snowCfg.riskSetting.assetType,
		assetCount: snowCfg.riskSetting.assetCount,
		runBy: snowCfg.riskSetting.runBy,
		status: snowCfg.riskSetting.status,

		lofCfgCategories: snowCfg.LOFCategories.map(cat => ({
			...cat,
			components: cat.components.map(comp => ({
				...comp,
				inspectionType: 'PACP',
			})),
		})),

		cofCfgCategories: snowCfg.COFCategories,
		riskType: snowCfg.riskSetting.riskType ?? 'linear',
		matrix: snowCfg.riskSetting.matrix ?? { ...defaultRiskMatrix },
		maxOverallRiskScore: snowCfg.riskSetting.maxOverallRiskScore,
		postProcessing: snowCfg.riskSetting.postProcessing,
	};

	return result;
};

function* watchGetRiskConfig() {
	yield takeLatest(getRiskConfig, getRiskConfigSaga);
}

interface ConfigData {
	errorMessage: string;
}

export function* createRiskConfigSaga(action: AnyAction) {
	try {
		const config: CreateConfig = {
			...action.payload.config,
			publishState: 'unpublished',
		};

		const response: AxiosResponse = yield call(createRiskConfigApi, config);
		yield put(setRiskConfigNameError(t('')));

		action.payload.history.push(
			addRouteNamespace(`results/${response.data.insertedId}`),
		);
		yield put(setRiskConfigIsCreating(false));
		yield put(changeRiskConfigsPage(action.payload.page));
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const axiosError = e as AxiosError;
			const data = axiosError.response?.data as ConfigData;
			if (data?.errorMessage === '[400] Duplicate config name') {
				yield put(setRiskConfigNameError(t('Enter a unique name')));
			} else {
				yield put(
					error(
						t('Failed to create risk model: {{info}}', {
							info:
								axiosError.response?.statusText ??
								data?.errorMessage ??
								t('No additional information'),
						}),
					),
				);
			}
		} else {
			yield put(
				error(
					t('Failed to create risk model: {{info}}', {
						info: t('No additional information'),
					}),
				),
			);
		}
		yield put(setRiskConfigIsCreating(false));
	}
}

function* duplicateRiskConfigSaga(action: AnyAction) {
	try {
		const configId: string = action.payload.configId;
		const response: AxiosResponse = yield call(
			duplicateRiskConfigApi,
			configId,
		);
		yield put(setRiskConfigIsCreating(false));
		action.payload.onComplete(response.data.configId);
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const axiosError = e as AxiosError;
			const data = axiosError.response?.data as ConfigData;
			yield put(
				error(
					t('Failed to create risk model: {{info}}', {
						info:
							axiosError.response?.statusText ??
							data?.errorMessage ??
							t('No additional information'),
					}),
				),
			);
		} else {
			yield put(
				error(
					t('Failed to create risk model: {{info}}', {
						info: t('No additional information'),
					}),
				),
			);
		}
		yield put(setRiskConfigIsCreating(false));
	}
}

function* watchCreateRiskConfig() {
	yield takeLatest(duplicateRiskConfig, duplicateRiskConfigSaga);
	yield takeLatest(createRiskConfig, createRiskConfigSaga);
}

export function* renameRiskConfigSaga(action: PayloadAction<RiskConfig>) {
	try {
		yield put(setRiskIsSaving(true));

		const snowRiskConfig = action.payload;
		const mongoRiskConfig = mapSnowCfgToMongoCfg(snowRiskConfig);

		yield call(saveRiskConfigApi, mongoRiskConfig);
		yield put(success(t('Risk model renamed')));
		yield put(getRiskConfigResolved(snowRiskConfig));
		yield put(setRiskIsSaving(false));
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const axiosError = e as AxiosError;
			const data = axiosError.response?.data as ConfigData;
			if (data?.errorMessage === '[400] Duplicate config name') {
				yield put(setRiskConfigNameError(t('Enter a unique name')));
			} else {
				yield put(
					error(
						t('Risk model failed to save: {{info}}', {
							info:
								axiosError.response?.statusText ??
								data?.errorMessage ??
								t('No additional information'),
						}),
					),
				);
			}
		} else {
			yield put(
				error(
					t('Risk model failed to save: {{info}}', {
						info: t('No additional information'),
					}),
				),
			);
		}
		yield put(setRiskIsSaving(false));
	}
}

export function* saveRiskConfigSaga(action: PayloadAction<RiskConfig>) {
	try {
		yield put(setRiskIsSaving(true));

		const snowRiskConfig = action.payload;
		const mongoRiskConfig = mapSnowCfgToMongoCfg(snowRiskConfig);

		yield call(saveRiskConfigApi, mongoRiskConfig);
		yield put(success(t('Risk model saved')));
		yield put(setRiskSetupDirty(false));
		yield put(setRiskIsSaving(false));
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const axiosError = e as AxiosError;
			const data = axiosError.response?.data as ConfigData;
			if (data?.errorMessage === '[400] Duplicate config name') {
				yield put(setRiskConfigNameError(t('Enter a unique name')));
			} else {
				yield put(
					error(
						t('Risk model failed to save: {{info}}', {
							info:
								axiosError.response?.statusText ??
								data?.errorMessage ??
								t('No additional information'),
						}),
					),
				);
			}
		} else {
			yield put(
				error(
					t('Risk model failed to save: {{info}}', {
						info: t('No additional information'),
					}),
				),
			);
		}
		yield put(setRiskIsSaving(false));
	}
}

function* watchStoreRiskConfig() {
	yield takeLatest(renameRiskConfigAction, renameRiskConfigSaga);
	yield takeLatest(saveRiskConfigAction, saveRiskConfigSaga);
}

function* getRiskDetailsSaga() {
	try {
		yield put(setRiskDetailsIsBusy(true));
		const query: string = yield select(riskDetailsQuerySelector);
		const payload: AxiosResponse<ResolvedResponse> = yield retry(
			2,
			1500,
			getRiskDetailsApi,
			query,
		);
		const rows = payload.data.data as Row[];
		const data = {
			...payload.data,
			data: rows.map(riskDetail => {
				if (riskDetail.ASSET_ID.indexOf('#') > 0) {
					return {
						...riskDetail,
						ASSET_ID: riskDetail.ASSET_ID.split('#')[1],
					};
				} else {
					return riskDetail;
				}
			}),
		};
		yield put(getRiskDetailsResolved(data));
		yield put(setRiskDetailsIsBusy(false));
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const axiosError = e as AxiosError;
			yield put(error(t('Failed to get risk details')));
			yield put(
				getRiskDetailsRejected({
					code: axiosError.response?.status ?? -1,
					text:
						axiosError.response?.statusText ??
						t('No additional information'),
				}),
			);
		}
		yield put(setRiskDetailsIsBusy(false));
	}
}
function* watchGetRiskDetails() {
	yield takeLatest(
		[
			getRiskDetails,
			reloadRiskDetails,
			changePage,
			changeSlice,
			changePagination,
			changeSort,
			changeFilterSelect,
			changeFiltersSelect,
		],
		getRiskDetailsSaga,
	);
}

function* getLogSaga(action: AnyAction) {
	try {
		const configId: string = action.payload;
		const response: AxiosResponse<LogReply> = yield retry(
			5,
			1500,
			getRiskCalcLogApi,
			configId,
		);
		yield put(getLogResolved(response.data));
	} catch (e) {
		yield put(getLogRejected());
	}
}
function* watchGetLog() {
	yield takeLatest(getLog, getLogSaga);
}

function* runRiskSaga(action: AnyAction) {
	try {
		const configId: string = action.payload;
		const riskSettingResponse: AxiosResponse<RiskRunReply> = yield retry(
			1, // Take this down to 1 retry for the 404 fix
			1500,
			startRiskCalculationApi,
			configId,
		);
		const reply = riskSettingResponse.data;
		yield put(runRiskResolved());
		switch (reply.calcStatus) {
			case 'risk-calc-in-progress':
			case 'risk-calc-post-processing-normalize':
				yield put(error(t('Risk calculation already in progress')));
				break;
			default:
				yield put(success(t('Risk calculation requested')));
				break;
		}
	} catch (e) {
		yield put(runRiskRejected());
	}
}
function* watchRunRisk() {
	yield takeLatest(runRisk, runRiskSaga);
}

function* resetRiskSaga(action: AnyAction) {
	try {
		const configId: string = action.payload;
		yield retry(
			1, // Take this down to 1 retry for the 404 fix
			1500,
			resetRiskCalculationApi,
			configId,
		);

		yield put(setRiskRunning(false));
		yield put(resetRiskResolved());
		yield put(success(t('Risk calculation cancel requested')));
	} catch (e) {
		yield put(setRiskRunning(false));
		yield put(resetRiskRejected());
		yield put(success(t('Risk calculation failed')));
	}
}
function* watchResetRisk() {
	yield takeLatest(resetRisk, resetRiskSaga);
}

function* deleteRiskSaga(action: AnyAction) {
	try {
		yield retry(3, 1500, deleteRiskApi, action.payload.configId);
		yield put(success(t('Risk model deleted successfully')));
		yield put(setRiskIsDeleting(false));
		action.payload.refresh();
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const axiosError = e as AxiosError;
			yield put(
				error(
					t('Risk model failed to delete: {{info}}', {
						info:
							axiosError.response?.statusText ??
							t('No additional information'),
					}),
				),
			);
		} else {
			yield put(
				error(
					t('Risk model failed to delete: {{info}}', {
						info: t('No additional information'),
					}),
				),
			);
		}
		yield put(setRiskIsDeleting(false));
	}
}
function* watchDeleteRiskeSaga() {
	yield takeLatest(deleteRisk, deleteRiskSaga);
}

function* changeRiskConfigPublishStateSaga(action: AnyAction) {
	const { newPublishState, configId } = action.payload;
	try {
		yield call(changeRiskConfigPublishStateApi, newPublishState, configId);
		yield put(
			success(t('Risk model {{newPublishState}}', { newPublishState })),
		);
		action.payload.refresh();
	} catch (err) {
		yield put(
			error(
				t('Failed to {{newPublishState}} risk model', {
					newPublishState,
				}),
			),
		);
	}
}
function* watchChangeRiskConfigPublishStateSaga() {
	yield takeLatest(
		changeRiskConfigPublishState,
		changeRiskConfigPublishStateSaga,
	);
}

function* getRiskCountInspectionSaga(action: AnyAction) {
	const {
		key,
		metadata,
		metadataType,
		assetType,
		fieldName,
		inspectionType,
	} = action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountInspectionApi,
			metadata,
			metadataType,
			assetType,
			fieldName,
			inspectionType,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

function* getRiskCountAttributeSaga(action: AnyAction) {
	const { key, metadata, metadataType, assetType, fieldName } =
		action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountAtributeApi,
			metadata,
			metadataType,
			assetType,
			fieldName,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

function* getRiskCountObservationSaga(action: AnyAction) {
	const { key, metadata, metadataType, assetType, fieldName } =
		action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountObservationApi,
			metadata,
			metadataType,
			assetType,
			fieldName,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}
function* getRiskCountProximitySaga(action: AnyAction) {
	const { key, metadata, metadataType, assetType, layerName } =
		action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountProximityApi,
			metadata,
			metadataType,
			assetType,
			layerName,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

function* getRiskCountProximityAttributeSaga(action: AnyAction) {
	const {
		key,
		metadata,
		metadataType,
		assetType,
		layerName,
		fieldName,
		valueType,
		distance,
	} = action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountProximityAtributeApi,
			metadata,
			metadataType,
			assetType,
			layerName,
			fieldName,
			valueType,
			distance,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

function* getSimulationDataSaga() {
	const response: AxiosResponse<SimulationData> = yield retry(
		5,
		1500,
		getSimulationDataApi,
	);
	yield put(getSimulationDataResolved(response.data));
}

function* watchGetSimulationData() {
	yield takeLatest(getSimulationData, getSimulationDataSaga);
}

function* getTaskDataSaga(action: AnyAction) {
	const assetType = action.payload;

	const response: AxiosResponse<TaskData> = yield retry(
		5,
		1500,
		getTaskDataApi,
		assetType,
	);
	yield put(getTaskDataResolved(response.data));
}

function* watchGetTaskData() {
	yield takeLatest(getTaskData, getTaskDataSaga);
}

function* getRiskCountCustomTableSaga(action: AnyAction) {
	const {
		key,
		metadata,
		metadataType,
		assetType,
		fieldName,
		tableName,
		valueType,
	} = action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountCustomTableApi,
			metadata,
			metadataType,
			assetType,
			fieldName,
			tableName,
			valueType,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

function* getRiskCountTaskSaga(action: AnyAction) {
	const {
		key,
		metadata,
		metadataType,
		assetType,
		fieldName,
		tableName,
		valueType,
		inspectionType,
	} = action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountTaskApi,
			metadata,
			metadataType,
			assetType,
			fieldName,
			tableName,
			valueType,
			inspectionType,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

function* getRiskCountScoreMatrixSaga(action: AnyAction) {
	const { key, components, scoreMatrix, assetType, inspectionType } =
		action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountScoreMatrixApi,
			components,
			scoreMatrix,
			assetType,
			inspectionType,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

function* watchGetRiskCount() {
	yield takeLatest(getRiskCountInspection, getRiskCountInspectionSaga);
	yield takeLatest(getRiskCountAttribute, getRiskCountAttributeSaga);
	yield takeLatest(getRiskCountProximity, getRiskCountProximitySaga);
	yield takeLatest(getRiskCountObservation, getRiskCountObservationSaga);

	yield takeLatest(
		getRiskCountProximityAttribute,
		getRiskCountProximityAttributeSaga,
	);
	yield takeLatest(getRiskCountCustomTable, getRiskCountCustomTableSaga);
	yield takeLatest(getRiskCountTask, getRiskCountTaskSaga);
	yield takeLatest(getRiskCountScoreMatrix, getRiskCountScoreMatrixSaga);
}

export function* initRiskMatrixPreviewSaga(action: AnyAction) {
	const {
		cofWeighting,
		lofWeighting,
		riskMethod,
		grades,
		grid,
		preview,
		configId,
		initialLoad,
	} = action.payload;
	try {
		yield put(setCancellPreview(false));
		yield put(setIsPreviewLoading(true));
		yield put(setIsPreviewOnLoad(initialLoad));
		yield put(setMatrixPreviewStatus('started'));
		const previewPostresponse: AxiosResponse<InitRiskMatrixPreviewResponse> =
			yield call(
				initRiskPreviewApi,
				cofWeighting,
				lofWeighting,
				riskMethod,
				grades,
				grid,
				preview,
				configId,
			);
		if (
			previewPostresponse?.data &&
			[
				'risk-calc-matrix-preview-start',
				'risk-calc-matrix-preview-in-progress',
				'risk-calc-matrix-preview-initiated',
			].includes(previewPostresponse?.data?.calcStatus)
		) {
			yield put(success(t('Risk Preview is being calculated.')));
			// This is needed so that the config has time to set its status to start.
			// Otherwise, when polling immediately, its in a previous completed state.
			yield delay(1000);
			yield put(getRiskMatrixPreview({ configId, initialLoad }));
		}
	} catch (err) {
		yield put(setCancellPreview(false));
		yield put(setEnablePreview(false));
		yield put(setIsPreviewLoading(false));
		yield put(setOnlyGradesUpdated(false));
		yield put(setDidMatrixUpdate(false));
		yield put(error(t('Preview calculation failed')));
	}
}

export function* cancelRiskMatrixPreviewSaga(action: AnyAction) {
	const { configId, matrix } = action.payload;
	// Use the configId to cancel the request on the server
	yield call(resetRiskPreviewCalculationApi, configId);

	// Set the results to what the preview was asked for
	yield put(setRiskMatrixAfterPreview(matrix));
	yield put(setCancellPreview(true));
	yield put(setEnablePreview(false));
	yield put(setIsPreviewLoading(false));
	yield put(setOnlyGradesUpdated(false));
	yield put(setDidMatrixUpdate(false));
	yield put(error(t('Preview calculation cancelled')));
}

export function* getRiskMatrixPreviewSaga(action: AnyAction) {
	const { configId, initialLoad } = action.payload;
	try {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const getPreviewResponse: AxiosResponse<any> = yield call(
			getRiskPreviewApi,
			configId,
		);

		if (getPreviewResponse?.data?.calcStatus) {
			yield put(
				setMatrixPreviewStatus(getPreviewResponse?.data?.calcStatus),
			);
			if (
				getPreviewResponse?.data?.calcStatus ===
				'risk-calc-matrix-preview-failed'
			) {
				yield put(setEnablePreview(false));
				yield put(setIsPreviewLoading(false));
				yield put(setOnlyGradesUpdated(false));
				yield put(setDidMatrixUpdate(false));
				yield put(setRiskSetupDirty(false));
				yield put(error(t('Preview calculation failed')));
			}
			if (
				getPreviewResponse?.data?.calcStatus ===
				'risk-calc-matrix-preview-complete'
			) {
				yield put(setCancellPreview(false));
				yield put(setEnablePreview(false));
				yield put(setIsPreviewLoading(false));
				if (
					getPreviewResponse?.data?.preview?.grades?.cofRanges
						?.length > 0 &&
					getPreviewResponse?.data?.preview?.grades?.lofRanges
						?.length > 0 &&
					getPreviewResponse?.data?.preview?.grid?.length > 0
				) {
					yield put(
						setRiskMatrixAfterPreview(
							getPreviewResponse.data.preview,
						),
					);
					yield put(setOnlyGradesUpdated(false));
					yield put(setDidMatrixUpdate(true));
					if (!initialLoad) yield put(setRiskSetupDirty(true));
				} else {
					yield put(setOnlyGradesUpdated(false));
					yield put(setDidMatrixUpdate(false));
					if (!initialLoad) yield put(setRiskSetupDirty(true));
					yield put(error(t('Preview calculation failed')));
				}
			}
		}
	} catch (err) {
		yield put(setCancellPreview(false));
		yield put(setEnablePreview(false));
		yield put(setIsPreviewLoading(false));
		yield put(setOnlyGradesUpdated(false));
		yield put(setDidMatrixUpdate(false));
		if (!initialLoad) yield put(setRiskSetupDirty(true));
		yield put(error(t('Preview calculation failed')));
	}
}

function* watchGetRiskMatrix() {
	yield takeLatest(initRiskMatrixPreview, initRiskMatrixPreviewSaga);
	yield takeLatest(cancelRiskMatrixPreview, cancelRiskMatrixPreviewSaga);
	yield takeLatest(getRiskMatrixPreview, getRiskMatrixPreviewSaga);
}

const sagaArray = [
	watchRunRisk(),
	watchCreateRiskConfig(),
	watchGetRiskConfig(),
	watchStoreRiskConfig(),
	watchGetRiskDetails(),
	watchGetLog(),
	watchResetRisk(),
	watchDeleteRiskeSaga(),
	watchChangeRiskConfigPublishStateSaga(),
	watchGetRiskCount(),
	watchGetRiskMatrix(),
	watchGetSimulationData(),
	watchGetTaskData(),
];

export default sagaArray;
